// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {ColorPickerField} from '@app/features/ui/components/form/ColorPickerField';
import * as UserCommands from '@app/features/user/commands/UserCommands';
import * as UserProfileCommands from '@app/features/user/commands/UserProfileCommands';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useState} from 'react';

const logger = new Logger('ThemeColorSection');

const DEFAULT_THEME_COLOR = 0x4641d9;

const THEME_COLOR_DESCRIPTOR = msg({
	message: 'Theme color',
	comment: 'Short label for the theme color picker in appearance settings. Keep it concise.',
});
const PICK_THE_ACCENT_COLOR_USED_FOR_YOUR_PROFILE_DESCRIPTOR = msg({
	message: 'Pick the accent color used for your profile. Available to everyone.',
	comment: 'Description for the theme color picker in appearance settings.',
});

export const THEME_COLOR_SECTION_ID = 'theme-color';

export const THEME_COLOR_SECTION_TITLE = THEME_COLOR_DESCRIPTOR;

export const ThemeColorSection: React.FC = observer(() => {
	const {i18n} = useLingui();
	const currentUser = Users.currentUser;
	const serverAccentColor = typeof currentUser?.accentColor === 'number' ? currentUser.accentColor : null;
	const [accentColor, setAccentColor] = useState<number | null>(serverAccentColor);
	useEffect(() => {
		setAccentColor(serverAccentColor);
	}, [serverAccentColor]);
	const handleChange = useCallback(
		async (value: number | null) => {
			const previous = accentColor;
			setAccentColor(value);
			try {
				const updated = await UserCommands.update({accent_color: value});
				const saved = typeof updated.accent_color === 'number' ? updated.accent_color : null;
				setAccentColor(saved);
				UserProfileCommands.clearCurrentUserProfiles();
			} catch (error) {
				logger.warn('Failed to update theme color', error);
				setAccentColor(previous);
			}
		},
		[accentColor],
	);
	if (!currentUser) {
		return null;
	}
	return (
		<div data-flx="user.appearance-tab.theme-color-section.div">
			<ColorPickerField
				label={i18n._(THEME_COLOR_DESCRIPTOR)}
				description={i18n._(PICK_THE_ACCENT_COLOR_USED_FOR_YOUR_PROFILE_DESCRIPTOR)}
				value={accentColor ?? DEFAULT_THEME_COLOR}
				onChange={(value: number) => void handleChange(value)}
				defaultValue={DEFAULT_THEME_COLOR}
				isDefaultValue={accentColor === null}
				onReset={() => void handleChange(null)}
				data-flx="user.appearance-tab.theme-color-section.color-picker-field.change"
			/>
		</div>
	);
});
